import { Country } from '@prisma/client';
import { Country as CountryInfo, State } from 'country-state-city';

export function getStateOptions(
  country: Country = Country.US
): { name: string; code: string }[] {
  const USStatesToBeRemoved = [
    'AS', // American Samoa
    'UM-81', // Baker Island
    'GU', // Guam
    'UM-84', // Howland Island
    'UM-86', // Jarvis Island
    'UM-67', // Johnston Atoll
    'UM-89', // Kingman Reef
    'UM-71', // Midway Atoll
    'UM-76', // Navassa Island
    'MP', // Northern Mariana Islands
    'UM-95', // Palmyra Atoll
    'PR', // Puerto Rico
    'UM', // United States Minor Outlying Islands
    'VI', // United States Virgin Islands
    'UM-79', // Wake Island
  ];

  return State.getStatesOfCountry(country)
    .filter(
      (state) =>
        // Only include states with 2-character codes and not in removal list
        state.isoCode.length && !USStatesToBeRemoved.includes(state.isoCode)
    )
    .map((state) => ({
      code: state.isoCode,
      name: state.name,
    }));
}

export function getCountryOptions(): { name: string; code: Country }[] {
  return Object.values(Country)
    .filter((countryCode) => {
      const country = CountryInfo.getCountryByCode(countryCode);
      // Only include countries with 2-character codes and at least one state
      return (
        country &&
        countryCode.length === 2 &&
        getStateOptions(countryCode).length > 0
      );
    })
    .sort((countryCodeA, countryCodeB) => {
      const countryA = CountryInfo.getCountryByCode(countryCodeA);
      const countryB = CountryInfo.getCountryByCode(countryCodeB);
      return countryA.name > countryB.name ? 1 : -1;
    })
    .map((countryCode) => {
      const country = CountryInfo.getCountryByCode(countryCode);
      return {
        code: countryCode,
        name: country.name,
      };
    });
}

export function getAllCountryOptions(): { name: string; code: Country }[] {
  return Object.values(Country)
    .filter((countryCode) => {
      const country = CountryInfo.getCountryByCode(countryCode);
      // Only include countries with 2-character codes and at least one state
      return country && countryCode.length === 2;
    })
    .sort((countryCodeA, countryCodeB) => {
      const countryA = CountryInfo.getCountryByCode(countryCodeA);
      const countryB = CountryInfo.getCountryByCode(countryCodeB);
      return countryA.name > countryB.name ? 1 : -1;
    })
    .map((countryCode) => {
      const country = CountryInfo.getCountryByCode(countryCode);
      return {
        code: countryCode,
        name: country.name,
      };
    });
}

/**
 * Resolves an ISO-3166 country code to its `country-state-city` display
 * name. Falls back to the code itself when the package can't resolve it
 * (e.g. legacy values) so the caller never renders nothing.
 */
export const getCountryName = (
  code: string | null | undefined
): string | undefined => {
  if (!code) return undefined;
  return CountryInfo.getCountryByCode(code)?.name ?? code;
};
